import Export from "@/components/Export";
import { FilterBarMultiSelect } from "@/components/FilterBar";
import SearchBox from "@/components/SearchBox";
import { useReportsSearch } from "@/hooks/useReportsSearch";
import { getPaginationData } from "@/utils/kendo";
import animationData from "@/animation.json";
import { CustomDialogBox } from "@/components/Dialog";
import { ACCEPTED, ASSIGNED, AVAILABLE } from "@/constants/area";
import { LookupValueMapping } from "@/constants/lookups";
import { RELEASE_PENDING, RELEASED } from "@/constants/request";
import { OVERDUE, REJECTED } from "@/constants/tasks";
import { useCompaniesWithUsers } from "@/hooks/useCompanies";
import { useDistinctActiveLevelNames } from "@/hooks/useLevels";
import { useAllLookups } from "@/hooks/useLookups";
import { useReports } from "@/hooks/useReports";
import { ICompanyWithUsers } from "@/interfaces/company.interface";
import { ILookup } from "@/interfaces/lookups.interface";
import { ICompanyUser } from "@/interfaces/request.interface";
import reportViewService, {
  ICreateReportView,
  IReportView,
} from "@/services/report-view.service";
import reportService, { IReport } from "@/services/report.service";
import { useAppSelector } from "@/store/hooks/hooks";
import { RootState } from "@/store/store";
import ToastService from "@/utils/toast";
import { Controls, Player } from "@lottiefiles/react-lottie-player";
import { Button } from "@progress/kendo-react-buttons";
//@ts-ignore
import { SUBCONTRACTOR } from "@/constants/roles";
//@ts-ignore
import { PagerTargetEvent } from "@progress/kendo-react-data-tools/pager/Pager";
import {
  ComboBox,
  DropDownList
} from "@progress/kendo-react-dropdowns";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
  GridToolbar,
  isColumnMenuFilterActive,
  isColumnMenuSortActive,
} from "@progress/kendo-react-grid";
import { ChevronDown, ChevronUp, Filter, Save } from "lucide-react";
import React, { createRef, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Filters from "../../constants/filters";
import IPageState from "../../interfaces/page.interface";
import { columnInterface, generateColumns } from "./columns";
import NameDialog from "./NameDialog";

export const Listing = () => {
  const _exporter = createRef<ExcelExport>();
  const Pagination = getPaginationData();

  // Default column order
  const defaultColumnOrder = [
    "companyName",
    "levelName",
    "subcontractorName",
    "activityName",
    "areaName",
    "aarNumber",
    "wirNumber",
    "areaStatus",
    "aarStatus",
    "assignmentDate",
    "startDate",
    "endDate",
    "assignedBy",
    "remarks",
  ];

  const [columnsMapping, setColumnsMapping] = useState<any>(defaultColumnOrder);
  const [page, setPage] = useState<IPageState>(Pagination.initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();
  const [showFilters, setShowFilters] = useState(false);

  // Area Statuses
  const areaStatuses = useMemo(
    () => [
      { label: "Available", value: AVAILABLE },
      { label: "Assigned", value: ASSIGNED },
      { label: "Released", value: RELEASED },
    ],
    []
  );

  // AAR Statuses
  const aarStatuses = useMemo(
    () => [
      { label: "Accepted", value: ACCEPTED },
      { label: "Assigned", value: ASSIGNED },
      { label: "Rejected", value: REJECTED },
      { label: "Released", value: RELEASED },
      { label: "Overdue", value: OVERDUE },
      { label: "Released – Pending WIR", value: RELEASE_PENDING },
    ],
    []
  );

  // Initialize columns with empty data first
  const [stateColumns, setStateColumns] = useState<any>(() =>
    generateColumns(() => {}, null, [], [], [], [], areaStatuses, aarStatuses)
  );
  // Stores the saved column config (show/hide state) from loadView or onColumnsSubmit.
  // The column init effect uses this to regenerate columns with up-to-date filter data.
  const [columnViewConfig, setColumnViewConfig] = useState<any>(null);

  // Filter states
  const [selectedCompanies, setSelectedCompanies] = useState<any[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<any>(null);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [selectedAreaStatus, setSelectedAreaStatus] = useState<any>(null);
  const [selectedAARStatus, setSelectedAARStatus] = useState<any>(null);

  // Filtered data states
  const [filteredCompanies, setFilteredCompanies] = useState<
    ICompanyWithUsers[]
  >([]);
  const [filteredLevels, setFilteredLevels] = useState<string[]>([]);
  const [filteredSubcons, setFilteredSubcons] = useState<ICompanyUser[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ILookup[]>([]);
  const [activitiesOptions, setActivitiesOptions] = useState<ILookup[]>([]);
  const [assignedSubcon, setAssignedSubcon] = useState<ICompanyUser[]>([]);

  // View management states
  const [views, setViews] = useState<IReportView[]>([]);
  const [selectedView, setSelectedView] = useState<IReportView | null>(null);
  const [savedViewState, setSavedViewState] = useState<any>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewDialogMode, setViewDialogMode] = useState<"saveAs" | "rename">(
    "saveAs"
  );
  const [viewDropdownData, setViewDropdownData] = useState<any[]>([]);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { user } = useAppSelector((state: RootState) => state.user);
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );
  const dispatch = useDispatch();

  // Reset page to first when search changes
  const resetPage = useCallback(() => {
    setPage(Pagination.initialDataState);
  }, [Pagination.initialDataState]);

  // Search functionality
  const { searchValue, searchFilter, handleSearchChange } = useReportsSearch({
    searchFields: [
      "companyName",
      "levelName",
      "areaName",
      "aarNumber",
      "wirNumber",
    ],
    onSearchChange: resetPage,
  });

  // Grid data state (separate from search)
  const [dataState, setDataState] = useState<any>(Pagination.initialDataState);

  // Fetch companies
  const { data: companies } = useCompaniesWithUsers(
    selectedProject?.id!,
    {},
    {
      enabled: !!selectedProject?.id,
    }
  );

  // Fetch distinct active level names for reports
  const { data: levelsData } = useDistinctActiveLevelNames(
    selectedProject?.id || "",
    {
      enabled: !!selectedProject?.id && !!user,
    }
  );

  // Fetch activities
  const { data: activitiesData } = useAllLookups(
    {
      filter: {
        logic: "and",
        filters: [
          {
            field: "type",
            operator: "eq",
            value: LookupValueMapping.Activities,
          },
        ],
      },
    },
    { enabled: true }
  );

  // Build grid filters from toolbar selections
  // Structure matches Kendo Grid's filter format for proper checkmark display
  const gridFilters = useMemo(() => {
    const filters: any[] = [];

    // Company filter - use camelCase field name and "or" logic with "eq" operators for each value
    if (selectedCompanies.length > 0) {
      filters.push({
        logic: "or",
        filters: selectedCompanies.map((c: any) => ({
          field: "companyName",
          operator: "eq",
          value: c.name,
        })),
      });
    }

    // Level filter - use camelCase field name and "or" logic with "eq" operators for each value
    if (selectedLevels.length > 0) {
      filters.push({
        logic: "or",
        filters: selectedLevels.map((levelName: string) => ({
          field: "levelName",
          operator: "eq",
          value: levelName,
        })),
      });
    }

    // Subcontractor filter - single value with "or" wrapper
    if (selectedSubcontractor) {
      filters.push({
        logic: "or",
        filters: [
          {
            field: "subcontractorName",
            operator: "eq",
            value: selectedSubcontractor.name,
          },
        ],
      });
    }

    // Activity filter - single value with "or" wrapper
    if (selectedActivity) {
      filters.push({
        logic: "or",
        filters: [
          {
            field: "activityName",
            operator: "eq",
            value: selectedActivity.value,
          },
        ],
      });
    }

    // Area Status filter - single value with "or" wrapper
    if (selectedAreaStatus) {
      filters.push({
        logic: "or",
        filters: [
          {
            field: "areaStatus",
            operator: "eq",
            value: selectedAreaStatus.value,
          },
        ],
      });
    }

    // AAR Status filter - single value with "or" wrapper
    if (selectedAARStatus) {
      filters.push({
        logic: "or",
        filters: [
          {
            field: "aarStatus",
            operator: "eq",
            value: selectedAARStatus.value,
          },
        ],
      });
    }

    return filters;
  }, [
    selectedCompanies,
    selectedLevels,
    selectedSubcontractor,
    selectedActivity,
    selectedAreaStatus,
    selectedAARStatus,
  ]);

  // Update grid's filter state when toolbar filters change
  useEffect(() => {
    const toolbarFields = [
      "companyName",
      "levelName",
      "subcontractorName",
      "activityName",
      "areaStatus",
      "aarStatus",
    ];

    // Get existing grid column filters (not from toolbar)
    const existingColumnFilters =
      dataState.filter?.filters?.filter((f: any) => {
        // Check if this is a nested filter (has filters array)
        if (f.filters && Array.isArray(f.filters)) {
          // Check if any of the nested filters are from toolbar
          const isToolbarFilter = f.filters.some(
            (nested: any) =>
              nested.field && toolbarFields.includes(nested.field)
          );
          return !isToolbarFilter;
        }
        return !(f.field && toolbarFields.includes(f.field));
      }) || [];

    // Combine toolbar filters with column filters
    const allFilters = [...gridFilters, ...existingColumnFilters];

    // Only update if the filters actually changed
    const currentFiltersJson = JSON.stringify(dataState.filter?.filters || []);
    const newFiltersJson = JSON.stringify(allFilters);

    if (currentFiltersJson !== newFiltersJson) {
      setDataState({
        ...dataState,
        filter:
          allFilters.length > 0
            ? {
                logic: "and",
                filters: allFilters,
              }
            : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridFilters]);

  // Sync grid column filters back to toolbar filter states
  useEffect(() => {
    // Don't sync while loading a view - let the load complete first
    if (isLoadingView) return;
    if (!dataState.filter?.filters) return;

    const filters = dataState.filter.filters;

    // Helper to extract filter values from grid filters
    const getFilterValues = (field: string) => {
      for (const filter of filters) {
        // Check if it's a CompositeFilterDescriptor
        if ("filters" in filter && Array.isArray(filter.filters)) {
          const values = filter.filters
            .filter((f: any) => "field" in f && f.field === field)
            .map((f: any) => f.value);
          if (values.length > 0) return values;
        }
        // Check if it's a FilterDescriptor
        if ("field" in filter && filter.field === field) {
          return [filter.value];
        }
      }
      return [];
    };

    // Update company selection
    const companyValues = getFilterValues("companyName");
    if (companyValues.length > 0 && companies?.data) {
      const selected = companies.data.filter((c) =>
        companyValues.includes(c.name)
      );
      if (JSON.stringify(selected) !== JSON.stringify(selectedCompanies)) {
        setSelectedCompanies(selected);
      }
    } else if (companyValues.length === 0 && selectedCompanies.length > 0) {
      setSelectedCompanies([]);
    }

    // Update level selection
    const levelValues = getFilterValues("levelName");

    if (levelValues.length > 0 && levelsData?.data) {
      const selected = levelsData.data.filter((l) => levelValues.includes(l));
      if (JSON.stringify(selected) !== JSON.stringify(selectedLevels)) {
        setSelectedLevels(selected);
      }
    } else if (levelValues.length === 0 && selectedLevels.length > 0) {
      setSelectedLevels([]);
    }

    // Update subcontractor selection
    const subconValues = getFilterValues("subcontractorName");
    if (subconValues.length > 0 && assignedSubcon) {
      const selected = assignedSubcon.find((s) => s.name === subconValues[0]);
      if (selected && selected !== selectedSubcontractor) {
        setSelectedSubcontractor(selected);
      }
    } else if (subconValues.length === 0 && selectedSubcontractor !== null) {
      setSelectedSubcontractor(null);
    }

    // Update activity selection
    const activityValues = getFilterValues("activityName");
    if (activityValues.length > 0 && activitiesOptions) {
      const selected = activitiesOptions.find(
        (a) => a.value === activityValues[0]
      );
      if (selected && selected !== selectedActivity) {
        setSelectedActivity(selected);
      }
    } else if (activityValues.length === 0 && selectedActivity !== null) {
      setSelectedActivity(null);
    }

    // Update area status selection
    const areaStatusValues = getFilterValues("areaStatus");
    if (areaStatusValues.length > 0) {
      const selected = areaStatuses.find(
        (s) => s.value === areaStatusValues[0]
      );
      if (selected && selected !== selectedAreaStatus) {
        setSelectedAreaStatus(selected);
      }
    } else if (areaStatusValues.length === 0 && selectedAreaStatus !== null) {
      setSelectedAreaStatus(null);
    }

    // Update AAR status selection
    const aarStatusValues = getFilterValues("aarStatus");
    if (aarStatusValues.length > 0) {
      const selected = aarStatuses.find((s) => s.value === aarStatusValues[0]);
      if (selected && selected !== selectedAARStatus) {
        setSelectedAARStatus(selected);
      }
    } else if (aarStatusValues.length === 0 && selectedAARStatus !== null) {
      setSelectedAARStatus(null);
    }
  }, [
    dataState.filter,
    companies?.data,
    levelsData?.data,
    assignedSubcon,
    activitiesOptions,
    areaStatuses,
    aarStatuses,
    isLoadingView,
  ]);

  // Sync filters immediately after view loading completes
  useEffect(() => {
    if (!isLoadingView && dataState.filter?.filters) {
      const filters = dataState.filter.filters;

      // Helper to extract filter values
      const getFilterValues = (field: string) => {
        for (const filter of filters) {
          if ("filters" in filter && Array.isArray(filter.filters)) {
            const values = filter.filters
              .filter((f: any) => "field" in f && f.field === field)
              .map((f: any) => f.value);
            if (values.length > 0) return values;
          }
          if ("field" in filter && filter.field === field) {
            return [filter.value];
          }
        }
        return [];
      };

      // Sync companies
      const companyValues = getFilterValues("companyName");
      if (companyValues.length > 0 && companies?.data) {
        const selected = companies.data.filter((c) =>
          companyValues.includes(c.name)
        );
        setSelectedCompanies(selected);
      } else if (companyValues.length === 0) {
        setSelectedCompanies([]);
      }

      // Sync levels
      const levelValues = getFilterValues("levelName");
      if (levelValues.length > 0 && levelsData?.data) {
        const selected = levelsData.data.filter((levelName) =>
          levelValues.includes(levelName)
        );
        setSelectedLevels(selected);
      } else if (levelValues.length === 0) {
        setSelectedLevels([]);
      } // Sync subcontractor
      const subconValues = getFilterValues("subcontractorName");
      if (subconValues.length > 0 && assignedSubcon) {
        const selected = assignedSubcon.find((s) => s.name === subconValues[0]);
        if (selected) setSelectedSubcontractor(selected);
      } else if (subconValues.length === 0) {
        setSelectedSubcontractor(null);
      }

      // Sync activity
      const activityValues = getFilterValues("activityName");
      if (activityValues.length > 0 && activitiesOptions) {
        const selected = activitiesOptions.find(
          (a) => a.value === activityValues[0]
        );
        if (selected) setSelectedActivity(selected);
      } else if (activityValues.length === 0) {
        setSelectedActivity(null);
      }

      // Sync area status
      const areaStatusValues = getFilterValues("areaStatus");
      if (areaStatusValues.length > 0) {
        const selected = areaStatuses.find(
          (s) => s.value === areaStatusValues[0]
        );
        if (selected) setSelectedAreaStatus(selected);
      } else if (areaStatusValues.length === 0) {
        setSelectedAreaStatus(null);
      }

      // Sync AAR status
      const aarStatusValues = getFilterValues("aarStatus");
      if (aarStatusValues.length > 0) {
        const selected = aarStatuses.find(
          (s) => s.value === aarStatusValues[0]
        );
        if (selected) setSelectedAARStatus(selected);
      } else if (aarStatusValues.length === 0) {
        setSelectedAARStatus(null);
      }
    }
  }, [
    isLoadingView,
    dataState.filter,
    levelsData?.data,
    companies?.data,
    assignedSubcon,
    activitiesOptions,
    areaStatuses,
    aarStatuses,
  ]);

  // Update saved state after view loading completes and syncing is done
  useEffect(() => {
    if (isLoadingView) {
      // Wait for next tick to allow all sync effects to complete
      const timer = setTimeout(() => {
        setSavedViewState({
          filter: normalizeState(dataState.filter),
          sort: normalizeState(dataState.sort),
          columns: columnsMapping ? JSON.stringify(stateColumns) : null,
          columnOrder: columnsMapping ? JSON.stringify(columnsMapping) : null,
        });
        setIsLoadingView(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [
    isLoadingView,
    dataState.filter,
    dataState.sort,
    columnsMapping,
    stateColumns,
  ]);

  // Helper function to format date filter values to YYYY-MM-DD in local timezone
  const formatDateFilters = (filters: any[]): any[] => {
    return filters.map((filter: any) => {
      // Handle nested filters recursively
      if (filter.filters && Array.isArray(filter.filters)) {
        return {
          ...filter,
          filters: formatDateFilters(filter.filters),
        };
      }

      // Format date fields
      if (
        filter.field &&
        ["assignmentDate", "startDate", "endDate"].includes(filter.field)
      ) {
        if (filter.value) {
          // Handle both Date object and string
          const date =
            filter.value instanceof Date
              ? filter.value
              : new Date(filter.value);

          if (!isNaN(date.getTime())) {
            // Get the local date the user selected (already in local timezone since it's a Date object)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");

            return {
              ...filter,
              value: `${year}-${month}-${day}`,
            };
          }
        }
      }
      return filter;
    });
  };

  // Combine toolbar filters, grid column filters, and search filter for API call
  const combinedFilter = useMemo(() => {
    const allFilters: any[] = [];

    // Add grid filters (toolbar + column filters)
    if (dataState.filter?.filters?.length) {
      const formattedFilters = formatDateFilters(dataState.filter.filters);
      allFilters.push(...formattedFilters);
    }

    // Add search filter
    if (searchFilter) {
      allFilters.push(searchFilter);
    }

    if (allFilters.length === 0) return undefined;

    return {
      logic: "and" as const,
      filters: allFilters,
    };
  }, [dataState.filter, searchFilter]);

  // Fetch reports data - only after view is loaded
  const {
    data: reportsData,
    isLoading,
    refetch,
  } = useReports(
    {
      projectId: selectedProject?.id || "",
      pageNumber: page.skip,
      pageSize: page.take,
      sort: dataState.sort,
      filter: combinedFilter,
    },
    {
      enabled: !!selectedProject?.id && selectedView !== null,
      showLoader: true,
    }
  );

  // Determine companies relevant to the current user (subcontractors only see their own company)
  const userCompanies = useMemo(() => {
    if (!user || !companies?.data) return null;
    const isSubcontractor = user.roles?.includes(SUBCONTRACTOR);
    if (!isSubcontractor) return null;
    return companies.data.filter((company) =>
      company.users?.some((u) => u.id === user.id)
    );
  }, [user, companies?.data]);

  // Update filtered companies when companies data changes
  useEffect(() => {
    const source = userCompanies !== null ? userCompanies : (companies?.data || []);
    const sortedCompanies = [...source].sort((a, b) =>
      a.name?.trim().localeCompare(b.name?.trim())
    );
    setFilteredCompanies(sortedCompanies);
  }, [companies?.data, userCompanies]);

  // Update filtered levels when levels data changes
  useEffect(() => {
    const sorted = (levelsData?.data || []).sort((a, b) => a.localeCompare(b));
    setFilteredLevels(sorted);
  }, [levelsData?.data]);

  // Update filteredSubcons when assignedSubcon changes
  useEffect(() => {
    setFilteredSubcons(assignedSubcon || []);
  }, [assignedSubcon]);

  // Update filteredActivities when activitiesOptions changes
  useEffect(() => {
    setFilteredActivities(activitiesOptions || []);
  }, [activitiesOptions]);

  // Update assignedSubcon from companies
  useEffect(() => {
    if (companies?.data) {
      const sourceCompanies = userCompanies !== null ? userCompanies : companies.data;
      const allCompanyUsers = sourceCompanies.flatMap((company) =>
        (company.users || []).map((u) => ({ ...u }))
      );
      const unique = Array.from(
        new Map(allCompanyUsers.map((u) => [u.id, u])).values()
      ).sort((a, b) => a.name.localeCompare(b.name));
      setAssignedSubcon(unique);
    }
  }, [companies?.data, userCompanies]);


  // Update activities options from activities data
  useEffect(() => {
    if (activitiesData?.data) {
      let options = activitiesData.data.filter((x) => x.value);

      // Subcontractors: restrict to activities belonging to their company/companies
      if (userCompanies !== null) {
        const allowedActivityIds = new Set(
          userCompanies.flatMap((c) => c.activities || [])
        );
        options = options.filter((a) => a.id && allowedActivityIds.has(a.id));
      }

      setActivitiesOptions(options);
    }
  }, [activitiesData?.data, userCompanies]);

  // Initialize columns when data is available
  useEffect(() => {
    const cols = generateColumns(
      onColumnsSubmit,
      columnViewConfig ?? stateColumns, // Use saved view config when available
      filteredCompanies,
      filteredLevels,
      filteredSubcons,
      filteredActivities,
      areaStatuses,
      aarStatuses,
      columnsMapping // Pass column order
    );
    setStateColumns(cols);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnViewConfig, filteredCompanies, filteredLevels, filteredSubcons, filteredActivities]);

  const excelExport = async () => {
    if (!_exporter.current || !selectedProject?.id) return;

    try {
      // Fetch all data without pagination using the report service
      const result = await reportService.getReports({
        projectId: selectedProject.id,
        pageNumber: 0,
        pageSize: 999999, // Large number to get all records
        sort: dataState.sort,
        filter: combinedFilter,
      });

      if (result.data && result.data.length > 0) {
        // Keep dates as Date objects for Excel date filtering
        // Excel will apply its own date formatting and provide date-specific filters
        const formattedData = result.data.map((item: IReport) => ({
          ...item,
          assignmentDate: item.assignmentDate
            ? new Date(item.assignmentDate)
            : null,
          startDate: item.startDate ? new Date(item.startDate) : null,
          endDate: item.endDate ? new Date(item.endDate) : null,
        }));

        _exporter.current.save(formattedData);
        setTimeout(() => {
          ToastService.success("Report has been downloaded successfully.");
        }, 2000);
      }
    } catch (error) {
      console.error("Export failed:", error);
      ToastService.error("Failed to export report");
    }
  };

  // Fetch views only after dropdown data is loaded
  useEffect(() => {
    const hasAllDropdownData =
      companies?.data?.length &&
      levelsData?.data?.length &&
      activitiesData?.data?.length &&
      selectedProject?.id &&
      user?.id;

    if (hasAllDropdownData) {
      fetchViews();
    }
  }, [
    selectedProject?.id,
    user?.id,
    companies?.data,
    levelsData?.data,
    activitiesData?.data,
  ]);

  const fetchViews = async (viewId?: string) => {
    if (!selectedProject?.id) return;

    const result = await reportViewService.getViews(selectedProject.id);
    if (!result.error && result.data && Array.isArray(result.data)) {
      setViews(result.data);

      // Build dropdown data - default view first, then other views, then separator, then actions
      const defaultView = result.data.find((v: IReportView) => v.isDefault);
      const otherViews = result.data.filter((v: IReportView) => !v.isDefault);

      const temp: any[] = [];

      // Add default view first if it exists
      if (defaultView) {
        temp.push({
          ...defaultView,
          Name: defaultView.name,
          IsAction: false,
          IsSeparator: false,
        });
      }

      // Add other views
      otherViews.forEach((view: IReportView) => {
        temp.push({
          ...view,
          Name: view.name,
          IsAction: false,
          IsSeparator: false,
        });
      });

      temp.push({
        Name: "---",
        IsSeparator: true,
      });

      temp.push(
        {
          Name: "Rename",
          IsAction: true,
        },
        {
          Name: "Save",
          IsAction: true,
        },
        {
          Name: "Save As",
          IsAction: true,
        },
        {
          Name: "Delete",
          IsAction: true,
        }
      );

      setViewDropdownData(temp);

      // Select specific view or default view or first view
      const viewToSelect = viewId
        ? result.data.find((v: IReportView) => v.id === viewId)
        : result.data.find((v: IReportView) => v.isDefault) || result.data[0];

      if (viewToSelect) {
        setSelectedView(viewToSelect);
        loadView(viewToSelect);
      }
    }
  };

  // Helper function to convert date strings to Date objects in filter state
  const convertDateStringsToObjects = (filters: any[]): any[] => {
    return filters.map((filter: any) => {
      // Handle nested filters recursively
      if (filter.filters && Array.isArray(filter.filters)) {
        return {
          ...filter,
          filters: convertDateStringsToObjects(filter.filters),
        };
      }

      // Convert date field values from string to Date object
      if (
        filter.field &&
        ["assignmentDate", "startDate", "endDate"].includes(filter.field)
      ) {
        if (filter.value && typeof filter.value === "string") {
          return {
            ...filter,
            value: new Date(filter.value),
          };
        }
      }

      return filter;
    });
  };

  const loadView = (view: IReportView) => {
    setIsLoadingView(true);
    setSelectedView(view);

    // Parse all states
    let filterState = undefined;
    let sortState = undefined;

    // Load filter state
    if (view.filterState && view.filterState !== "{}") {
      try {
        filterState = JSON.parse(view.filterState);
        if (!filterState || Object.keys(filterState).length === 0) {
          filterState = undefined;
        } else if (filterState.filters && Array.isArray(filterState.filters)) {
          // Convert date strings to Date objects for Kendo DatePicker
          filterState.filters = convertDateStringsToObjects(
            filterState.filters
          );
        }
      } catch (error) {
        console.error("Error parsing filter state:", error);
        filterState = undefined;
      }
    }

    // Load sort state
    if (view.sortState && view.sortState !== "[]") {
      try {
        sortState = JSON.parse(view.sortState);
        if (!sortState || sortState.length === 0) {
          sortState = undefined;
        }
      } catch (error) {
        console.error("Error parsing sort state:", error);
        sortState = undefined;
      }
    }

    // Set both filter and sort in one update
    setDataState({
      ...dataState,
      filter: filterState,
      sort: sortState,
    });

    // Load column state and order
    if (view.columnState && view.columnState !== "{}") {
      try {
        const columnData = JSON.parse(view.columnState);

        // Check if it's the new format (object with columns and order)
        if (
          columnData &&
          typeof columnData === "object" &&
          "columns" in columnData &&
          "order" in columnData
        ) {
          // New format: has both columns and order
          // Set the order first
          if (Array.isArray(columnData.order) && columnData.order.length > 0) {
            setColumnsMapping(columnData.order);

            // Store column config - the column init useEffect will regenerate
            // columns with the correct (current) filtered data
            if (
              Array.isArray(columnData.columns) &&
              columnData.columns.length > 0
            ) {
              setColumnViewConfig(columnData.columns);
            }
          } else if (
            Array.isArray(columnData.columns) &&
            columnData.columns.length > 0
          ) {
            // No order saved, use default
            setColumnViewConfig(columnData.columns);
          }
        } else if (Array.isArray(columnData) && columnData.length > 0) {
          // Old format: just an array of columns (backward compatibility)
          onColumnsSubmit(columnData);
        }
      } catch (error) {
        console.error("Error parsing column state:", error);
      }
    }

    setHasUnsavedChanges(false);
  };

  const handleSaveView = async () => {
    if (!selectedView) return;

    // Combine column state and order into a single object
    const columnData = {
      columns: stateColumns,
      order: columnsMapping,
    };

    const currentState = {
      filterState: normalizeState(dataState.filter),
      sortState: normalizeState(dataState.sort),
      columnState: JSON.stringify(columnData),
    };

    const result = await reportViewService.updateView(
      selectedView.id,
      currentState
    );
    if (!result.error) {
      setSavedViewState({
        filter: currentState.filterState,
        sort: currentState.sortState,
        columns: currentState.columnState,
        columnOrder: columnsMapping ? JSON.stringify(columnsMapping) : null,
      });
      setHasUnsavedChanges(false);
      ToastService.success("View saved successfully!");
      fetchViews(selectedView.id); // Keep current view selected
    } else {
      ToastService.error("Failed to save view");
    }
  };

  const handleSaveAsView = async (name: string) => {
    if (!selectedProject?.id || !user?.id) return;

    // Combine column state and order into a single object
    const columnData = {
      columns: stateColumns,
      order: columnsMapping,
    };

    const createDto: ICreateReportView = {
      name,
      projectId: selectedProject.id,
      filterState: normalizeState(dataState.filter),
      sortState: normalizeState(dataState.sort),
      columnState: JSON.stringify(columnData),
      isDefault: false,
    };

    const result = await reportViewService.createView(createDto);
    if (!result.error && result.data) {
      setViewDialogOpen(false);
      ToastService.success("View created successfully!");
      fetchViews(result.data.id); // Switch to newly created view
    } else {
      ToastService.error("Failed to create view");
    }
  };

  const handleRenameView = async (newName: string) => {
    if (!selectedView) return;

    const result = await reportViewService.renameView(selectedView.id, newName);
    if (!result.error) {
      setViewDialogOpen(false);
      ToastService.success("View renamed successfully!");
      fetchViews(selectedView.id); // Keep current view selected
    } else {
      ToastService.error("Failed to rename view");
    }
  };

  const handleDeleteView = () => {
    if (!selectedView || selectedView.isDefault) return;
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async (action: string) => {
    setShowDeleteDialog(false);

    if (action === "yes" && selectedView) {
      const result = await reportViewService.deleteView(selectedView.id);
      if (!result.error) {
        ToastService.success("View deleted successfully!");
        fetchViews(); // After delete, let it select default view
      } else {
        ToastService.error("Failed to delete view");
      }
    }
  };

  const handleViewDialogSave = (name: string) => {
    if (viewDialogMode === "saveAs") {
      handleSaveAsView(name);
    } else {
      handleRenameView(name);
    }
  };

  const handleColumnReorder = (event: any) => {
    // Sort columns by orderIndex to get the actual order after reordering
    const sortedColumns = [...event.columns].sort(
      (a, b) => a.orderIndex - b.orderIndex
    );
    const columnNames = sortedColumns
      .map((col: any) => col.field)
      .filter((f: any) => f);

    if (columnNames && columnNames.length > 0) {
      // Just update the column mapping - don't regenerate columns
      // The Grid already handles the visual reordering
      // We only need to track the order for saving to the view
      setColumnsMapping(columnNames);
    }
  };

  // Helper function to normalize state for comparison
  const normalizeState = (state: any): string | null => {
    if (!state) return null;

    const stateStr = JSON.stringify(state);

    // Check if it's an empty filter object
    if (stateStr === '{"logic":"and","filters":[]}') {
      return null;
    }

    // Check if it's an empty sort array
    if (stateStr === "[]") {
      return null;
    }

    // Check if it's an empty object
    if (stateStr === "{}") {
      return null;
    }

    return stateStr;
  };

  // Detect changes to determine if Save button should be shown
  useEffect(() => {
    // Skip change detection while loading a view
    if (isLoadingView) return;

    if (!selectedView || !savedViewState) {
      setHasUnsavedChanges(false);
      return;
    }

    const currentState = {
      filter: normalizeState(dataState.filter),
      sort: normalizeState(dataState.sort),
      columns: columnsMapping ? JSON.stringify(stateColumns) : null,
      columnOrder: columnsMapping ? JSON.stringify(columnsMapping) : null,
    };

    const hasChanges =
      currentState.filter !== savedViewState.filter ||
      currentState.sort !== savedViewState.sort ||
      currentState.columns !== savedViewState.columns ||
      currentState.columnOrder !== savedViewState.columnOrder;

    setHasUnsavedChanges(hasChanges);
  }, [
    dataState,
    columnsMapping,
    stateColumns,
    selectedView,
    savedViewState,
    isLoadingView,
  ]);

  const getSelectedViewName = () => {
    if (!viewDropdownData?.length || !selectedView) return "";
    const view = views.find((v: IReportView) => v.id === selectedView.id);
    return `${hasUnsavedChanges ? "* " : ""}${view?.name || ""}`;
  };

  const handleAction = (item: any) => {
    switch (item.Name) {
      case "Rename":
        if (selectedView && !selectedView.isDefault) {
          setViewDialogMode("rename");
          setViewDialogOpen(true);
        } else {
          ToastService.error("Cannot rename the default view");
        }
        break;

      case "Save":
        handleSaveView();
        break;

      case "Save As":
        setViewDialogMode("saveAs");
        setViewDialogOpen(true);
        break;

      case "Delete":
        if (selectedView && !selectedView.isDefault) {
          handleDeleteView();
        } else {
          ToastService.error("Cannot delete the default view");
        }
        break;
    }
  };

  const handleViewChange = (e: any) => {
    const item = e.value;

    if (item.IsAction || item.IsSeparator) {
      if (item.IsAction) {
        handleAction(item);
      }
      return;
    }

    // Load selected view
    const view = views.find((v) => v.id === item.id);
    if (view) {
      setSelectedView(view);
      loadView(view);
    }
  };

  const itemRender = (li: any, itemProps: any) => {
    if (itemProps.dataItem.IsSeparator) {
      return <li className="k-item k-separator my-1" role="separator"></li>;
    }

    const handleItemClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleViewChange({ value: itemProps.dataItem });
    };

    return (
      <li className="cursor-pointer py-1 px-2" onClick={handleItemClick}>
        {itemProps.dataItem.Name || itemProps.dataItem.name}
      </li>
    );
  };

  function onColumnsSubmit(columnsState: Array<columnInterface>) {
    // Extract column order from the columnsState (preserves reordering)
    const columnOrder: string[] = columnsState
      .map((col: any) => col.field)
      .filter((f: any) => f);

    // Keep only visible columns for mapping (for backward compatibility)
    const visibleColumns: any = [];
    for (let col of columnsState) {
      if (col.show) {
        visibleColumns.push(col.field);
      }
    }

    setColumnsMapping(columnOrder);
    // Keep columnViewConfig in sync so the column init effect uses the latest show/hide state
    setColumnViewConfig(columnsState);

    // Also regenerate immediately for a snappy UI response
    const cols = generateColumns(
      onColumnsSubmit,
      columnsState,
      filteredCompanies,
      filteredLevels,
      filteredSubcons,
      filteredActivities,
      areaStatuses,
      aarStatuses,
      columnOrder // Pass column order
    );

    setStateColumns(cols);
  }

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? reportsData?.total || 0 : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    // Don't interfere with Grid's filter management
    // Just set the dataState directly - toolbar filters will be merged via the gridFilters useEffect
    setDataState(event.dataState);
  };

  // Filter handlers
  const handleCompanyFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase().trim() || "";
    if (!filterValue) {
      const sortedCompanies = (companies?.data || []).sort((a, b) =>
        a.name?.trim().localeCompare(b.name?.trim())
      );
      setFilteredCompanies(sortedCompanies);
    } else {
      const filtered = (companies?.data || [])
        .filter((c) => c.name?.trim().toLowerCase().includes(filterValue))
        .sort((a, b) => a.name?.trim().localeCompare(b.name?.trim()));
      setFilteredCompanies(filtered);
    }
  };

  const handleLevelFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase() || "";
    if (!filterValue) {
      const sorted = (levelsData?.data || []).sort((a, b) =>
        a.localeCompare(b)
      );
      setFilteredLevels(sorted);
    } else {
      const filtered = (levelsData?.data || [])
        .filter((levelName) => levelName.toLowerCase().includes(filterValue))
        .sort((a, b) => a.localeCompare(b));
      setFilteredLevels(filtered);
    }
  };

  const handleSubconFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase() || "";
    if (!filterValue) {
      setFilteredSubcons(assignedSubcon || []);
    } else {
      setFilteredSubcons(
        (assignedSubcon || []).filter((u) =>
          u.name?.toLowerCase().includes(filterValue)
        )
      );
    }
  };

  const handleActivitiesFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase() || "";
    if (!filterValue) {
      setFilteredActivities(activitiesOptions || []);
    } else {
      setFilteredActivities(
        (activitiesOptions || []).filter((a) =>
          a.value.toLowerCase().includes(filterValue)
        )
      );
    }
  };

  const getColIndex = (field: string) => {
    // Get only visible columns in their current order
    const visibleColumnsInOrder = columnsMapping.filter((fieldName: string) => {
      const col = stateColumns.find((c: any) => c.field === fieldName);
      return col?.show;
    });
    // Return the index among visible columns only to avoid gaps in orderIndex
    return visibleColumnsInOrder.findIndex((x: string) => x === field);
  };

  // Check if a column is active (filtered or sorted)
  const isColumnActive = (field: string) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  // Generate a unique key for the Grid based on visible columns to force re-render
  const gridKey = useMemo(() => {
    const visibleFields = stateColumns
      .filter((col: any) => col.show)
      .map((col: any) => col.field)
      .join(",");
    return `grid-${visibleFields}`;
  }, [stateColumns]);

  return (
    <>
      <style>{`
        /* Override the toolbar fixed height for multiselect */
        .k-toolbar .k-multiselect.reports-multiselect {
          height: auto !important;
        }
        .k-toolbar .k-combobox.reports-multiselect {
          height: auto !important;
        }
        .reports-multiselect .k-input-values {
          max-height: none !important;
          overflow: visible !important;
          flex-wrap: wrap !important;
        }
        .reports-multiselect .k-chip-list {
          flex-wrap: wrap !important;
        }
        .reports-multiselect .k-button {
          height: 32px !important;
        }
        .k-list-item:has(.custom-dp-item) {
            color: black;
            border-color: #555555;
            background-color: white;
        }
        .k-grid-norecords {
          max-width: 80vw !important;
        }
        .dropdown-btn .k-dropdownlist {
            background-color: #0078d4;
            border-color: #0078d4;
            border-radius: 100px;
            color: #fff !important;
        }
        /* Active column header styling */
        th.k-header.active .k-grid-column-menu {
            color: #fff;
            background-color: #0078d4;
        }
        .k-grid .k-grid-header-menu {
            padding-bottom: 25px;
            display: inline-block;
            width: 27px;
            height: 16px;
            background-repeat: no-repeat;
            background-size: 100% 100%;
        }
        /* Fix header and body table synchronization + enable horizontal scroll */
        .setting-page .k-grid .k-grid-header table,
        .setting-page .k-grid .k-grid-content table {
          table-layout: fixed !important;
          min-width: 100%;
        }
        .setting-page .k-grid .k-grid-header-wrap {
          overflow-x: hidden;
        }
        .setting-page .k-grid .k-grid-content {
          overflow-x: auto !important;
        }
      `}</style>
      <div className="setting-page" style={{ position: "relative" }}>
        {isLoading && (
          <div className="fixed inset-0 flex items-center justify-center bg-white/20 z-[100003]">
            <Player autoplay loop src={animationData} className="w-[80px]">
              <Controls
                visible={false}
                buttons={["play", "repeat", "frame", "debug"]}
              />
            </Player>
          </div>
        )}
        <Grid
          key={gridKey}
          style={{ height: "calc(100vh - 200px)" }}
          data={reportsData?.data || []}
          {...dataState}
          onDataStateChange={dataStateChange}
          skip={page.skip}
          take={page.take}
          total={reportsData?.total || 0}
          pageable={{
            ...Pagination.pageSizeValue,
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          className={`customHeight employee`}
          resizable={Filters.resizeable}
          scrollable="scrollable"
          selectable={{
            enabled: true,
            drag: true,
            mode: "multiple",
          }}
          reorderable={true}
          onColumnReorder={handleColumnReorder}
        >
          <GridToolbar>
            <div className="flex flex-col gap-3 w-full">
              {/* First Row - Search, Filter Button, Export */}
              <div className="flex gap-3 w-full items-center">
                <div className="w-[250px]">
                  <SearchBox
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder="Search by keyword"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center gap-2 px-3 py-[6px] border border-[#E2E8F0] rounded bg-white hover:bg-gray-50 transition-colors h-[34px] font-normal text-sm"
                >
                  <Filter size={16} />
                  {showFilters ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
                <div
                  className={`sm:ml-0 ml-auto sm:flex-initial flex-1 ${
                    !reportsData?.data || reportsData.data.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  onClick={
                    reportsData?.data && reportsData.data.length > 0
                      ? excelExport
                      : undefined
                  }
                >
                  <Export />
                  <ExcelExport
                    data={[]}
                    fileName={`AAR_Report_${
                      new Date().toISOString().split("T")[0]
                    }.xlsx`}
                    ref={_exporter}
                    filterable={true}
                  >
                    {stateColumns &&
                      stateColumns.length > 0 &&
                      stateColumns.map((column: any) => {
                        if (column.show) {
                          // Set specific widths for columns
                          let columnWidth = 150;
                          if (column.field === "wirNumber") columnWidth = 120;
                          else if (column.field === "aarNumber")
                            columnWidth = 120;
                          else if (column.field === "levelName")
                            columnWidth = 100;
                          else if (column.field === "areaStatus")
                            columnWidth = 100;
                          else if (column.field === "aarStatus")
                            columnWidth = 100;
                          else if (column.field === "areaName")
                            columnWidth = 120;
                          else if (column.field === "companyName")
                            columnWidth = 200;
                          else if (column.field === "subcontractorName")
                            columnWidth = 200;
                          else if (column.field === "activityName")
                            columnWidth = 200;
                          else if (column.field === "assignedBy")
                            columnWidth = 180;
                          else if (column.field === "remarks")
                            columnWidth = 250;
                          else if (
                            column.field === "assignmentDate" ||
                            column.field === "startDate" ||
                            column.field === "endDate"
                          )
                            columnWidth = 180;

                          // Check if this is a date column and set format
                          const isDateColumn = [
                            "assignmentDate",
                            "startDate",
                            "endDate",
                          ].includes(column.field);

                          // Use different format for startDate and endDate (no time)
                          const isDateOnlyColumn = [
                            "startDate",
                            "endDate",
                          ].includes(column.field);

                          return (
                            <ExcelExportColumn
                              key={column.field}
                              field={column.field}
                              title={column.title}
                              width={columnWidth}
                              cellOptions={
                                isDateColumn
                                  ? {
                                      format: isDateOnlyColumn
                                        ? "dd-mm-yyyy"
                                        : "dd-mm-yyyy hh:mm AM/PM",
                                    }
                                  : undefined
                              }
                            />
                          );
                        }
                      })}
                  </ExcelExport>
                </div>

                {/* View Management - Right Side */}
                <div className="ml-auto flex gap-x-2">
                  {hasUnsavedChanges && (
                    <Button
                      className="h-[34px] 2xl:px-3 px-2 sm:w-auto w-full"
                      onClick={handleSaveView}
                      themeColor="primary"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Save
                          className="w-4 h-4 sm:inline-block"
                          strokeWidth={1}
                        />
                        <span>Save</span>
                      </span>
                    </Button>
                  )}
                  <div className="dropdown-btn">
                    <DropDownList
                      className="inline-flex max-w-[350px] min-w-[120px] px-[16px] w-[100%_!important]"
                      data={viewDropdownData}
                      value={getSelectedViewName()}
                      itemRender={itemRender}
                      onChange={handleViewChange}
                      popupSettings={{
                        width: "300px",
                        popupClass: "cstPopup",
                        anchorAlign: {
                          horizontal: "right",
                          vertical: "bottom",
                        },
                        popupAlign: {
                          horizontal: "right",
                          vertical: "top",
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Second Row - Filters (Collapsible) */}
              {showFilters && (
                <div className="flex gap-3 w-full items-start flex-wrap">
                  <FilterBarMultiSelect
                    options={filteredCompanies.map((c) => ({
                      label: c.name,
                      value: c.id,
                    }))}
                    value={selectedCompanies.map((c: any) => c.id)}
                    onChange={(values) => {
                      const selected = filteredCompanies.filter((c) =>
                        values.includes(c.id)
                      );
                      setSelectedCompanies(selected);
                    }}
                    placeholder="Select Companies..."
                    maxVisibleTags={2}
                  />
                  <FilterBarMultiSelect
                    options={filteredLevels.map((l) => ({
                      label: l,
                      value: l,
                    }))}
                    value={selectedLevels}
                    onChange={(values) => setSelectedLevels(values)}
                    placeholder="Select Levels..."
                    maxVisibleTags={2}
                  />
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    data={filteredSubcons}
                    textField="name"
                    dataItemKey="id"
                    value={selectedSubcontractor}
                    onChange={(e) => setSelectedSubcontractor(e.value)}
                    placeholder="Select Subcontractor..."
                    style={{ width: "250px" }}
                    clearButton={true}
                    filterable={true}
                    onFilterChange={handleSubconFilterChange}
                    className="bg-white reports-multiselect"
                  />
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    data={filteredActivities}
                    textField="value"
                    dataItemKey="id"
                    value={selectedActivity}
                    onChange={(e) => setSelectedActivity(e.value)}
                    placeholder="Select Activity..."
                    style={{ width: "200px" }}
                    clearButton={true}
                    filterable={true}
                    onFilterChange={handleActivitiesFilterChange}
                    className="bg-white reports-multiselect"
                  />
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    data={areaStatuses}
                    textField="label"
                    dataItemKey="value"
                    value={selectedAreaStatus}
                    onChange={(e) => setSelectedAreaStatus(e.value)}
                    placeholder="Select Area Status..."
                    style={{ width: "180px" }}
                    clearButton={true}
                    className="bg-white reports-multiselect"
                  />
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    data={aarStatuses}
                    textField="label"
                    dataItemKey="value"
                    value={selectedAARStatus}
                    onChange={(e) => setSelectedAARStatus(e.value)}
                    placeholder="Select AAR Status..."
                    style={{ width: "180px" }}
                    clearButton={true}
                    className="bg-white reports-multiselect"
                  />
                </div>
              )}
            </div>
          </GridToolbar>
          {stateColumns &&
            stateColumns.length > 0 &&
            stateColumns.map((column: any) => {
              if (column.show) {
                if (column.cells) {
                  return (
                    <Column
                      key={column.field}
                      field={column.field}
                      title={column.title}
                      columnMenu={column.columnMenu}
                      minResizableWidth={150}
                      cells={column.cells}
                      width={150}
                      filter={column.filter ? column.filter : "text"}
                      orderIndex={getColIndex(column.field)}
                      headerClassName={
                        isColumnActive(column.field) ? "active" : ""
                      }
                    />
                  );
                }
                return (
                  <Column
                    key={column.field}
                    field={column.field}
                    title={column.title}
                    columnMenu={column.columnMenu}
                    minResizableWidth={250}
                    width={250}
                    filter={column.filter ? column.filter : "text"}
                    orderIndex={getColIndex(column.field)}
                    headerClassName={
                      isColumnActive(column.field) ? "active" : ""
                    }
                  />
                );
              }
              return null;
            })}
        </Grid>
      </div>

      {/* Name Dialog for Save As and Rename */}
      <NameDialog
        isOpen={viewDialogOpen}
        mode={viewDialogMode}
        currentName={viewDialogMode === "rename" ? selectedView?.name : ""}
        onClose={() => setViewDialogOpen(false)}
        onSave={handleViewDialogSave}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <CustomDialogBox
          dialogInfo={{
            title: "Delete View",
            message: `Are you sure you want to delete the view "${selectedView?.name}"?`,
          }}
          onToggleDeleteDialog={handleConfirmDelete}
        />
      )}
    </>
  );
};
